import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Download, LogOut, Inbox, LogIn, PenLine } from "lucide-react";
import { PROFILE } from "../data/content";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { label: "About", href: "#about" },
  { label: "Approach", href: "#approach" },
  { label: "Expertise", href: "#expertise" },
  { label: "Experience", href: "#experience" },
  { label: "Contact", href: "#contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, loading, login, logout } = useAuth();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const AuthControls = ({ mobile = false }) => {
    if (loading) return null;
    if (user) {
      return (
        <div className={mobile ? "flex flex-col gap-4" : "flex items-center gap-4"}>
          {user.is_owner && (
            <Link
              to="/admin/blog"
              data-testid="nav-write"
              onClick={() => setOpen(false)}
              className={`inline-flex items-center gap-2 font-mono tracking-[0.2em] uppercase text-[#2C5F3A] hover:text-[#367A49] transition-colors ${mobile ? "text-sm" : "text-[11px]"}`}
            >
              <PenLine size={mobile ? 16 : 14} /> Write
            </Link>
          )}
          {user.is_owner && (
            <Link
              to="/inquiries"
              data-testid="nav-inquiries"
              onClick={() => setOpen(false)}
              className={`inline-flex items-center gap-2 font-mono tracking-[0.2em] uppercase text-[#2C5F3A] hover:text-[#367A49] transition-colors ${mobile ? "text-sm" : "text-[11px]"}`}
            >
              <Inbox size={mobile ? 16 : 14} /> Inquiries
            </Link>
          )}
          <div className="flex items-center gap-3">
            {user.picture ? (
              <img src={user.picture} alt={user.name || user.email} className="w-8 h-8 rounded-full border border-[#16281C]/20 object-cover" />
            ) : (
              <span className="w-8 h-8 rounded-full bg-[#2C5F3A] text-[#EFEBDE] flex items-center justify-center font-display font-semibold text-sm">
                {(user.name || user.email || "?").charAt(0).toUpperCase()}
              </span>
            )}
            <span className="hidden lg:block font-mono text-[11px] tracking-[0.1em] text-[#4C5C50] max-w-[120px] truncate">
              {user.name || user.email}
            </span>
            <button
              onClick={logout}
              data-testid="logout-button"
              aria-label="Sign out"
              className="text-[#8B978C] hover:text-[#2C5F3A] transition-colors"
            >
              <LogOut size={mobile ? 20 : 16} />
            </button>
          </div>
        </div>
      );
    }
    return (
      <button
        onClick={login}
        data-testid="signin-button"
        className={`inline-flex items-center gap-2 font-mono tracking-[0.2em] uppercase text-[#16281C] hover:text-[#2C5F3A] transition-colors ${mobile ? "text-sm" : "text-[11px]"}`}
      >
        <LogIn size={mobile ? 16 : 14} /> Sign in
      </button>
    );
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, delay: isHome ? 1.4 : 0.1, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-500 ${
        scrolled || !isHome
          ? "bg-[#EFEBDE]/85 backdrop-blur-xl border-[#16281C]/10"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-mark" className="flex items-center gap-3 group">
          <span className="font-display font-bold text-lg tracking-tight bg-[#2C5F3A] text-[#EFEBDE] w-9 h-9 flex items-center justify-center">
            AC
          </span>
          <span className="hidden sm:block font-mono text-[11px] tracking-[0.2em] uppercase text-[#4C5C50] group-hover:text-[#16281C] transition-colors">
            Andres Campos
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {isHome &&
            NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#4C5C50] hover:text-[#2C5F3A] transition-colors"
              >
                {n.label}
              </a>
            ))}
          <Link
            to="/blog"
            data-testid="nav-blog"
            className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#4C5C50] hover:text-[#2C5F3A] transition-colors"
          >
            Blog
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-5">
          <AuthControls />
          {isHome && (
            <>
              <a
                href={PROFILE.resumeUrl}
                download
                data-testid="header-resume"
                className="inline-flex items-center gap-2 border border-[#16281C]/20 text-[#16281C] font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-3 hover:border-[#2C5F3A] hover:text-[#2C5F3A] transition-colors"
              >
                <Download size={14} /> Résumé
              </a>
              <a
                href="#contact"
                data-testid="header-cta"
                className="inline-flex items-center gap-2 bg-[#2C5F3A] text-[#EFEBDE] font-mono text-[11px] tracking-[0.2em] uppercase px-5 py-3 hover:bg-[#367A49] transition-colors"
              >
                Let's Talk
              </a>
            </>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-[#16281C]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#EFEBDE]/97 backdrop-blur-xl border-t border-[#16281C]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {isHome &&
                NAV.map((n) => (
                  <a
                    key={n.href}
                    href={n.href}
                    onClick={() => setOpen(false)}
                    className="font-display text-2xl tracking-tight text-[#16281C]"
                  >
                    {n.label}
                  </a>
                ))}
              <Link to="/blog" onClick={() => setOpen(false)} className="font-display text-2xl tracking-tight text-[#16281C]">
                Blog
              </Link>
              <div className="pt-2 border-t border-[#16281C]/10">
                <AuthControls mobile />
              </div>
              {isHome && (
                <>
                  <a
                    href={PROFILE.resumeUrl}
                    download
                    onClick={() => setOpen(false)}
                    className="border border-[#16281C]/20 text-[#16281C] font-mono text-xs tracking-[0.2em] uppercase px-5 py-4 text-center inline-flex items-center justify-center gap-2"
                  >
                    <Download size={15} /> Download Résumé
                  </a>
                  <a
                    href="#contact"
                    onClick={() => setOpen(false)}
                    className="bg-[#2C5F3A] text-[#EFEBDE] font-mono text-xs tracking-[0.2em] uppercase px-5 py-4 text-center"
                  >
                    Let's Talk
                  </a>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
